/*
This software is based on line-offset by Tom MacWright
Copyright (c) 2013 Tom MacWright https://github.com/tmcw/line-offset

All rights reserved.

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

- Redistributions of source code must retain the above copyright notice, this
  list of conditions and the following disclaimer.
- Redistributions in binary form must reproduce the above copyright notice, this
  list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.
- Neither the name "simple-statistics" nor the names of its contributors may be
  used to endorse or promote products derived from this software without
  specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR
ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON
ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

function clone(_) {
    return { x: _.x, y: _.y };
}

function explement_reflex_angle(angle) {
    if (angle > Math.PI) return angle - 2 * Math.PI;
    else if (angle < -Math.PI) return angle + 2 * Math.PI;
    else return angle;
}

function findVt(u1, u2, v1, v2) {
    var dx = v1.x - u1.x,
        dy = v1.y - u1.y,
        ux = u2.x - u1.x,
        uy = u2.y - u1.y,
        vx = v2.x - v1.x,
        vy = v2.y - v1.y,
        up, dn;

    up = ux * dy - dx * uy;
    dn = vx * uy - ux * vy;
    return up / dn;
}

function findUt(u1, u2, v1, v2, vt) {
    var dx = v1.x - u1.x,
        dy = v1.y - u1.y,
        ux = u2.x - u1.x,
        uy = u2.y - u1.y,
        vx = v2.x - v1.x,
        vy = v2.y - v1.y,
        up, dn;

    if (ux < -1e-6 || ux > 1e-6) {
        up = ux * dy - dx * uy;
        dn = vx * uy - ux * vy;
        return (vt * vx + dx) / ux;
    }

    if (uy < -1e-6 || uy > 1e-6) {
        up = uy * dx - dy * ux;
        dn = vy * ux - uy * vx;
        return (vt * vy + dy) / uy;
    }
}

function intersection(u1, u2, ut, v1, v2, vt) {
    var dx = v1.x - u1.x,
        dy = v1.y - u1.y,
        ux = u2.x - u1.x,
        uy = u2.y - u1.y,
        vx = v2.x - v1.x,
        vy = v2.y - v1.y,
        up, dn;
    // the first line is not vertical
    if (ux < -1e-6 || ux > 1e-6) {
        up = ux * dy - dx * uy;
        dn = vx * uy - ux * vy;
        return !(dn > -1e-6 && dn < 1e-6);
    }
    // the first line is not horizontal
    if (uy < -1e-6 || uy > 1e-6) {
        up = uy * dx - dy * ux;
        dn = vy * ux - uy * vx;
        return !(dn > -1e-6 && dn < 1e-6);
    }
    // the first line is too short
    return false;
}

export function lineoffset(vertices, offset) {
    if (offset === 0 || vertices.length < 2) return vertices;

    var output = [],
        v1 = vertices[0],
        v2 = vertices[1],
        half_turn_segments = 16,
        threshold = 8,
        angle_a = 0,
        angle_b = Math.atan2(v2.y - v1.y, v2.x - v1.x),
        w = { x: 0, y: 0 },
        joint_angle;

    // first vertex
    v1 = a(v1, angle_b, offset);
    output.push(clone(v1));

    // Sometimes when the first segment is too short, it causes ugly
    // curls at the beginning of the line. To avoid this, we make up
    // a fake vertex two offset-lengths before the first, and expect
    // intersection detection smoothes it out.
    var pre_first = dx(v1, -2 * Math.abs(offset), 0, angle_b);

    for (var i = 0; i < vertices.length - 1; i++) {
        v1 = vertices[i];
        v2 = vertices[i + 1];

        angle_a = angle_b;
        angle_b = Math.atan2(v2.y - v1.y, v2.x - v1.x);
        joint_angle = explement_reflex_angle(angle_b - angle_a);

        var half_turns = half_turn_segments * Math.abs(joint_angle),
            bulge_steps = 0;

        if (offset < 0) {
            if (joint_angle > 0) {
                joint_angle = joint_angle - 2 * Math.PI;
            } else {
                bulge_steps = 1 + Math.floor(half_turns / Math.PI);
            }
        } else {
            if (joint_angle < 0) {
                joint_angle = joint_angle + 2 * Math.PI;
            } else {
                bulge_steps = 1 + Math.floor(half_turns / Math.PI);
            }
        }

        w = ua(v1, angle_a, offset);
        output.push(clone(w));

        for (var s = 0; ++s < bulge_steps; ) {
            w = ua(v1,
                angle_a + (joint_angle * s) / bulge_steps,
                offset);
            output.push(clone(w));
        }

        v1 = a(v1, angle_b, offset);
    }

    // last vertex
    output.push(a(v2, angle_b, offset));

    //return selectVertices(output, offset, threshold);
    return output;
};

function selectVertices(output, offset, threshold) {
    var vx = [];

    for (var pos = 0; pos + 1 < output.length; pos++) {

        var pre = output[pos];
        var cur = output[++pos];

        var check_dist = offset * threshold,
            check_dist2 = check_dist * check_dist,
            t = 1, vt, ut;

        for (var i = pos; i + 1 < output.length; ++i) {
            var u0 = output[i],
                u1 = output[i + 1],
                dx = u0.x - cur.x,
                dy = u0.y - cur.y;

            if (dx * dx + dy * dy > check_dist2) break;
            if (!intersection(pre, cur, vt, u0, u1, ut)) {
                continue;
            } else {
                vt = findVt(pre, cur, u0, u1);
                ut = findUt(pre, cur, u0, u1, vt);
            }
            if (vt < 0 || vt > t || ut < 0 || ut > 1) continue;
            t = vt;
            pos = i + 1;
        }

        output[pos] = {
            x: pre.x + t * (cur.x - pre.x),
            y: pre.y + t * (cur.y - pre.y)
        };

        vx.push(clone(output[pos]));
    }

    return vx;
}
export function dx(v, dx, dy, a) {
    return {
        x: v.x + (dx * Math.cos(a) - dy * Math.sin(a)),
        y: v.y + (dx * Math.sin(a) + dy * Math.cos(a))
    };
};

export function a(v, a, offset) {
    return {
        x:  v.x + (offset * Math.sin(a)),
        y: v.y - (offset * Math.cos(a))
    };
};

export function ua(u, a, offset) {
    return {
        x:  u.x + (offset * Math.sin(a)),
        y: u.y - (offset * Math.cos(a))
    };
};

export function ab(v, a, b, offset) {
    var sa = offset * Math.sin(a),
        ca = offset * Math.cos(a),
        h = Math.tan(0.5 * (b - a));
    return {
        x: v.x + sa + h * ca,
        y: v.y - ca + h * sa
    };
};
